import type { ReactNode } from 'react';

import Button from './Button';
import { cx, focusRingClass } from './shared';
import {
  statusClass,
  statusIcon,
  statusLabel,
  type StatusState,
} from './statusSemantics';

export interface StatusNoticeProps {
  state: StatusState;
  title?: ReactNode;
  description: ReactNode;
  action?: ReactNode;
  technicalDetails?: ReactNode;
  announce?: boolean;
  className?: string;
  /**
   * The title's element. A notice is a status message, not a page section, so
   * its title is plain bold text unless it really heads a section: then pass
   * the level that fits the page's outline.
   */
  titleAs?: 'p' | 'h2' | 'h3' | 'h4';
}

export function StatusNotice({
  state,
  title,
  description,
  action,
  technicalDetails,
  announce = false,
  className,
  titleAs: TitleTag = 'p',
}: StatusNoticeProps) {
  const Icon = statusIcon[state];
  const isAssertive = state === 'failed' || state === 'denied' || state === 'invalid';

  return (
    <section
      data-so-status={state}
      role={announce ? (isAssertive ? 'alert' : 'status') : undefined}
      aria-live={announce ? (isAssertive ? 'assertive' : 'polite') : undefined}
      aria-atomic={announce || undefined}
      className={cx('rounded-surface border p-4', statusClass[state], className)}
    >
      <div className="flex items-start gap-field-gap">
        <Icon
          className={cx('mt-0.5 h-5 w-5 shrink-0', state === 'loading' && 'animate-spin')}
          aria-hidden="true"
        />
        <div className="min-w-0 flex-1">
          <TitleTag className="text-subsection-title font-semibold">
            {title ?? statusLabel[state]}
          </TitleTag>
          <div className="mt-1 text-body">{description}</div>
          {action ? <div className="mt-4">{action}</div> : null}
          {technicalDetails ? (
            <details className="mt-4 border-t border-current/30 pt-3">
              <summary
                className={cx(
                  focusRingClass,
                  'cursor-pointer rounded-control text-control font-semibold',
                )}
              >
                Technical details
              </summary>
              <div className="so-data-text mt-2 break-words text-meta">
                {technicalDetails}
              </div>
            </details>
          ) : null}
        </div>
      </div>
    </section>
  );
}

export const StatusRecoveryButton = Button;
